<template>
  <div class="StudyConversationMessageEditorTextDesktop" :class="classList">
    <ValidationProvider
      class="StudyConversationMessageEditorTextDesktop__textarea-wrapper"
      name="messageText"
      rules="required"
      immediate
    >
      <LLTextarea
        v-model="localMessage.text"
        size="compact"
        dynamic-size
        menu-bar-style="below"
        submit-on-mod-enter
        :placeholder="localMessage.files.length > 0 ? $t('add_comment_or_send') : $t('type_here')"
        data-e2e="questionInput"
        class="StudyConversationMessageEditorTextDesktop__textarea"
        @submit="onSendClick"
      >
        <template v-if="localMessage.files.length" #bottom-content>
          <LLFileSlider
            class="StudyConversationMessageEditorTextDesktop__files"
            :files.sync="localMessage.files"
            :editable="true"
          ></LLFileSlider>
        </template>
        <template v-if="isFileUploaderVisible || isActionsVisible" #additional-actions>
          <LLFileUploaderWrapper
            v-if="isFileUploaderVisible"
            :preparable="!localMessage.id"
            :study-id="localMessage.studyId"
            file-mime-type="image/*,image/heic,video/*"
            data-e2e="filesUploader"
            @upload="onUpload"
            @startUpload="onStartUpload"
            @prepare="onPrepare"
          >
            <LLButton round tertiary>
              <template #icon-left> <PhotoIcon class="text-status-03-600 w-5 h-5" /> </template>
            </LLButton>
          </LLFileUploaderWrapper>
          <LLUploadProgress
            class="StudyConversationMessageEditorTextDesktop__file-uploader-progress"
            :uploading-files-uuids.sync="uploadingFilesUuids"
          ></LLUploadProgress>
          <span class="StudyConversationMessageEditorTextDesktop__spacer"></span>
          <LLButton
            v-if="isAbleToCancel"
            class="StudyConversationMessageEditorTextDesktop__cancel"
            small
            secondary
            :disabled="isSendPending"
            @click="onCancelClick"
            >{{ $t('button_cancel') }}</LLButton
          >
          <LLButton
            v-if="isActionsVisible"
            round
            primary
            :loading="isSendPending"
            :disabled="!isValid"
            :aria-label="message.id ? $t('button_save') : $t('button_send')"
            @click="onSendClick"
          >
            <template #icon-left> <SendIcon /> </template>
          </LLButton>
        </template>
      </LLTextarea>
    </ValidationProvider>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import {
  StudyConversationMessageOptionsModel,
  StudyConversationMessageType
} from '@/models/studyConversationMessage/studyConversationMessageOptions'
import { StudyConversationMessageModel } from '@/models/studyConversationMessage'
import { QuestionModel } from '@/models/question'
import LLTextarea from '@/components/common/LLTextarea.vue'
import LLFileSlider from '@/components/common/LLFileSlider.vue'
import LLFileUploaderWrapper from '@/components/common/LLFileUploader/LLFileUploaderWrapper.vue'
import LLUploadProgress from '@/components/common/LLUploadProgress.vue'
import { ConversationMessageFileModel } from '@/models/conversationMessageFile'
import LLButton from '@/components/common/ui-components/LLButton.vue'
import PhotoIcon from '@/assets/icons/PhotoIcon.vue'
import SendIcon from '@/assets/icons/SendIcon.vue'

export default {
  name: 'StudyConversationMessageEditorTextDesktop',
  components: {
    SendIcon,
    PhotoIcon,
    LLButton,
    LLUploadProgress,
    LLFileUploaderWrapper,
    LLFileSlider,
    LLTextarea,
    ValidationProvider
  },
  props: {
    isCompactLayout: { type: Boolean, default: false },
    isMobileLayout: { type: Boolean, default: false },
    options: {
      type: StudyConversationMessageOptionsModel,
      default: () => new StudyConversationMessageOptionsModel()
    },
    message: { type: StudyConversationMessageModel, required: true },
    question: { type: QuestionModel, required: true },
    isSendPending: { type: Boolean, default: false },
    isValid: { type: Boolean, default: false },
    isAbleToCancel: { type: Boolean, default: false }
  },
  data() {
    return {
      localMessage: null,
      uploadingFilesUuids: []
    }
  },
  computed: {
    classList() {
      return {
        [`${this.$options.name}_screening`]: this.options.type === StudyConversationMessageType.SCREENING
      }
    },
    isActionsVisible() {
      return this.options.type !== StudyConversationMessageType.SCREENING
    },
    isFileUploaderVisible() {
      return this.options.type !== StudyConversationMessageType.SCREENING
    }
  },
  watch: {
    message: {
      handler() {
        if (!this.$_.isEqual(this.message, this.localMessage)) {
          this.localMessage = new StudyConversationMessageModel(this.message)
        }
      },
      deep: true,
      immediate: true
    },
    localMessage: {
      handler() {
        this.$emit('update:message', new StudyConversationMessageModel(this.localMessage))
      },
      deep: true
    }
  },
  methods: {
    onSendClick() {
      this.$emit('sendClick')
    },
    onCancelClick() {
      this.$emit('cancelClick')
    },
    onPrepare(file) {
      this.localMessage.files = [
        ...this.localMessage.files,
        new ConversationMessageFileModel({
          ...file,
          prospectId: this.message.prospectId,
          messageId: this.message.id,
          studyId: this.message.studyId
        })
      ]
    },
    onStartUpload(uploadingFilesUuids) {
      this.uploadingFilesUuids = uploadingFilesUuids
    },
    onUpload(fileList) {
      const convertedFileList = fileList.map(
        (file) =>
          new ConversationMessageFileModel({
            ...file,
            questionId: this.message.questionId,
            prospectId: this.message.prospectId,
            messageId: this.message.id,
            studyId: this.message.studyId
          })
      )
      this.localMessage.files = [...this.localMessage.files, ...convertedFileList]
    }
  }
}
</script>

<style scoped lang="scss">
.StudyConversationMessageEditorTextDesktop {
  $root: &;
  // No gray card/left-bar wrapper around the composer itself - the redesigned composer's
  // own border and rounded corners are the only framing, matching the prototype - #1684.
  &__spacer {
    @apply flex-1;
  }
  &__cancel {
    @apply mr-2;
  }
  &__file-uploader-progress {
    @apply ml-2;
  }
}
</style>
